import * as React from "react";
import {InputText} from "@bizform/primereact/components/inputtext/InputText";
import {InputTextProps} from "@bizform/primereact/inputtext";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {ComponentHelper, Debug} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type Props = InputTextProps & {
};

type WrappedCompState = {
    // attribute
    value?: string;
    tabindex?: number;
    readOnly?: boolean;
    disabled?: boolean;
    keyfilter?: any;
    validateOnly?: boolean;
    maxLength?: number;
    placeholder?: string;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    // event
    onChange?(e: React.FormEvent<HTMLInputElement>): void;
    onInput?(event: React.FormEvent<HTMLInputElement>): void;
    onKeyPress?(event: React.KeyboardEvent<HTMLInputElement>): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Jongkwang Park
 * @class BfInputText
 * @description BfInputText component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfInputText extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_RLq772PINziCt9FoqFQ8c6FOGewkoTQ",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.inputtext.label,
        description: I18n.componentElement.inputtext.description,
        icon: "",
        dataType: [DataType.STRING, DataType.NUMBER, DataType.INTEGER, DataType.FILE, DataType.USER],
        property: null
    };

    focus(): void {
        if (this._inputText) {
            this._inputText.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(property, this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        if (value !== this.state.wrappedCompState.value && ((this.dataType !== DataType.FILE && this.dataType !== DataType.USER || (!value && this.dataType === DataType.USER)))) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: ""
                }
            };
            newState.wrappedCompState.value = (value === null || value === undefined) ? "" : value;
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfInputText)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _inputText: HTMLInputElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                // must be assigned value property in the case of InputText
                // To avoid warning it("A component is changing an uncontrolled input of type undefined to be controlled.")
                // See defaultProps
                value: ""
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentWillUnmount(): void {
        this._inputText = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(event) {
        if (event.target.value !== this.state.wrappedCompState.value) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: event.target.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }


    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        return (
            <InputText
                {...newProps}
                id={this.elementId}
                key={key}
                ref={this._setRef}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{this.state.wrappedCompState.value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._inputText = ref.element;
            this.readyComponent();
        }
    }
}
